/**
 * AdminPage / AdminPageHeader — 본문 공통 컴포넌트 (우선순위 4: 페이지 타이틀 영역)
 *
 * 페이지명(타이틀), 설명, 우측 액션 버튼, 그리고 본문 간격을 자동으로 잡아준다.
 * 상위 layout 의 <main> 이 바깥 padding 을 담당하고, 이 컴포넌트는 그 안의
 * 세로 간격(space-y)과 헤더 레이아웃을 책임진다.
 *
 * 상호작용이 없으므로 서버 컴포넌트로 동작한다. (각 page.tsx 에서 바로 사용 가능)
 *
 * @example
 * // 올인원 사용
 * <AdminPage title="회원 관리" description="가입 회원을 관리합니다" actions={<Button>회원 등록</Button>}>
 *   ...본문...
 * </AdminPage>
 *
 * @example
 * // 헤더만 따로 쓰기
 * <AdminPageHeader title="대시보드" />
 */
import { type ReactNode } from "react";

import { cn } from "../../lib/utils";

export interface AdminPageHeaderProps {
  /** 페이지명 */
  title: ReactNode;
  /** 타이틀 아래 보조 설명 */
  description?: ReactNode;
  /** 우측 정렬 액션 영역 (등록/수정 버튼 등) */
  actions?: ReactNode;
  /** 타이틀 위 브레드크럼/뒤로가기 등 */
  breadcrumb?: ReactNode;
  className?: string;
}

export function AdminPageHeader({
  title,
  description,
  actions,
  breadcrumb,
  className,
}: AdminPageHeaderProps) {
    return (
        <div className={cn("flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between", className)}>
            <div className="space-y-1">
                {breadcrumb}
                <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
                {description && (
                <p className="text-sm text-muted-foreground">{description}</p>
                )}
            </div>
            {actions && (
                <div className="flex shrink-0 items-center gap-2">{actions}</div>
            )}
        </div>
    );
}

export interface AdminPageProps extends AdminPageHeaderProps {
  children: ReactNode;
  /** 헤더와 본문을 감싸는 바깥 컨테이너 클래스 */
  className?: string;
  /** 본문 영역 클래스 */
  contentClassName?: string;
}

export function AdminPage({
  title,
  description,
  actions,
  breadcrumb,
  className,
  contentClassName,
  children,
}: AdminPageProps) {
  return (
    <div className={cn("space-y-6", className)}>
      <AdminPageHeader
        title={title}
        description={description}
        actions={actions}
        breadcrumb={breadcrumb}
      />
      <div className={contentClassName}>{children}</div>
    </div>
  );
}
